/* ==========================================================================
   Cart Actions + Saved for Later
   Inline pill buttons (Save, Share) + Saved Items section
   ========================================================================== */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 749;
  const SAVED_ITEMS_KEY = 'dubiaroaches_saved_items';
  const CORSO_REMOVED_KEY = 'dubiaroaches_corso_removed';

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /* ========================================================================
     AJAX Section Rendering helpers (mirrors cart.js pattern)
     ======================================================================== */
  function getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      }
    ];
  }

  function getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  function renderSections(sections) {
    const cartItems = document.querySelector('cart-items');

    getSectionsToRender().forEach((section) => {
      const el =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      el.innerHTML = getSectionInnerHTML(sections[section.section], section.selector);
    });

    // Toggle is-empty class based on whether cart has items
    const parsedDoc = new DOMParser().parseFromString(
      sections[getSectionsToRender()[0].section], 'text/html'
    );
    const hasItems = parsedDoc.querySelector('.cart-items tbody tr') !== null;

    if (cartItems) cartItems.classList.toggle('is-empty', !hasItems);

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', !hasItems);

    if (typeof updateCheckoutButtonText === 'function') {
      updateCheckoutButtonText(sections[document.getElementById('main-cart-footer').dataset.id]);
    }

    if (window.DubiaCartCounts && sections['cart-icon-bubble']) {
      const cartItemCount = window.DubiaCartCounts.extractFromSectionHtml(sections['cart-icon-bubble']);
      if (cartItemCount !== null) window.DubiaCartCounts.update(cartItemCount);
    }
  }

  function sectionParams() {
    return {
      sections: getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname
    };
  }

  /* ========================================================================
     SavedItemsManager — localStorage persistence + UI rendering
     ======================================================================== */
  class SavedItemsManager {
    constructor() {
      this.container = document.getElementById('saved-items-section');
    }

    getAll() {
      try {
        return JSON.parse(localStorage.getItem(SAVED_ITEMS_KEY)) || [];
      } catch {
        return [];
      }
    }

    _save(items) {
      localStorage.setItem(SAVED_ITEMS_KEY, JSON.stringify(items));
    }

    add(item) {
      const items = this.getAll();
      if (items.some(i => i.variantId === item.variantId)) return;
      items.push(item);
      this._save(items);
      this.render();
    }

    remove(variantId) {
      const items = this.getAll().filter(i => i.variantId !== variantId);
      this._save(items);
      this.render();
    }

    clearAll() {
      this._save([]);
      this.render();
    }

    async addToCart(variantId, btn) {
      const items = this.getAll();
      const item = items.find(i => i.variantId === variantId);
      if (!item) return;

      const quantity = item.quantity || 1;
      const card = btn ? btn.closest('.saved-item') : null;

      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Adding\u2026';
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId), quantity: quantity }],
            ...sectionParams()
          })
        });

        if (!response.ok) throw new Error('Add to cart failed');
        const result = await response.json();

        // Animate saved item card out
        if (card) {
          card.classList.add('is-restoring');
          await new Promise(r => setTimeout(r, 250));
        }

        this.remove(variantId);
        renderSections(result.sections);
        showToast('Moved to cart');
      } catch (err) {
        console.error('SavedItems: add to cart error', err);
        showToast('Failed to add \u2014 try again');
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Move to cart';
        }
      }
    }

    render() {
      if (!this.container) return;
      const items = this.getAll();

      if (items.length === 0) {
        this.container.innerHTML = '';
        return;
      }

      const cards = items.map(item => {
        const price = item.price ? '$' + (parseInt(item.price) / 100).toFixed(2) : '';
        const variantLabel = item.variantTitle && item.variantTitle !== 'Default Title'
          ? item.variantTitle : '';
        const qty = item.quantity && item.quantity > 1 ? item.quantity : 0;

        return `
          <div class="saved-item" data-variant-id="${item.variantId}">
            ${item.image
              ? `<img class="saved-item__image" src="${item.image}" alt="" loading="lazy">`
              : `<div class="saved-item__image"></div>`
            }
            <div class="saved-item__info">
              <p class="saved-item__title">${escapeHtml(item.title)}</p>
              ${variantLabel ? `<p class="saved-item__variant">${escapeHtml(variantLabel)}</p>` : ''}
              <p class="saved-item__price">
                ${price}${qty ? `<span class="saved-item__qty">\u00d7${qty}</span>` : ''}
              </p>
            </div>
            <div class="saved-item__actions">
              <button type="button" class="saved-item__btn saved-item__btn--add" data-action="add-to-cart">Move to cart</button>
              <button type="button" class="saved-item__btn saved-item__btn--remove" data-action="remove" aria-label="Remove">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>`;
      }).join('');

      this.container.innerHTML = `
        <div class="saved-items">
          <div class="saved-items__header">
            <h3 class="saved-items__title">
              Saved for Later
              <span class="saved-items__count">${items.length}</span>
            </h3>
            <button type="button" class="saved-items__clear">Clear all</button>
          </div>
          <div class="saved-items__list">${cards}</div>
        </div>`;

      this.container.querySelectorAll('[data-action="add-to-cart"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const vid = btn.closest('.saved-item').dataset.variantId;
          this.addToCart(vid, btn);
        });
      });

      this.container.querySelectorAll('[data-action="remove"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const vid = btn.closest('.saved-item').dataset.variantId;
          this.remove(vid);
        });
      });

      const clearBtn = this.container.querySelector('.saved-items__clear');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => this.clearAll());
      }
    }
  }

  /* ========================================================================
     CartActionsManager — inline pill button handlers
     ======================================================================== */
  class CartActionsManager {
    constructor(savedItemsManager) {
      this.saved = savedItemsManager;
      this.init();
      this.observeMutations();
    }

    init() {
      const rows = document.querySelectorAll('#main-cart-items .js-contents tbody tr.cart-item');
      rows.forEach(tr => {
        if (tr.dataset.actionsbound) return;
        tr.dataset.actionsbound = '1';
        this.bindInlineActions(tr);
      });
    }

    bindInlineActions(tr) {
      const savePill = tr.querySelector('.cart-item-pill--save');
      const sharePill = tr.querySelector('.cart-item-pill--share');

      if (savePill) {
        savePill.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleSave(tr, savePill);
        });
      }

      // Desktop: inject "Save for later" text link into product details column
      const details = tr.querySelector('.cart-item__details');
      if (details && !details.querySelector('.cart-save-desktop')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-save-desktop';
        btn.textContent = '\u2661 Save for later';
        btn.addEventListener('click', () => this.handleSave(tr, btn));
        details.appendChild(btn);
      }

      const comparePill = tr.querySelector('.cart-item-pill--compare');
      if (comparePill) {
        comparePill.addEventListener('click', (e) => {
          e.preventDefault();
          // don't let this click reach document — the search modal's
          // outside-click handler would close it the instant it opens
          e.stopPropagation();
          this.handleCompare(tr);
        });
      }

      if (sharePill) {
        sharePill.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleShare(tr);
        });
      }
    }

    async handleSave(tr, pill) {
      const item = {
        variantId: tr.dataset.variantId,
        title: tr.dataset.itemTitle,
        variantTitle: tr.dataset.variantTitle,
        price: tr.dataset.itemPrice,
        image: tr.dataset.itemImage,
        productUrl: tr.dataset.productUrl,
        quantity: parseInt(tr.dataset.itemQuantity) || 1
      };

      // Disable pill and show loading text
      if (pill) {
        pill.disabled = true;
        pill.textContent = 'Saving\u2026';
      }

      this.saved.add(item);

      // Animate the cart item row out
      tr.classList.add('is-saving');

      const lineIndex = tr.dataset.lineIndex;

      try {
        const response = await fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: parseInt(lineIndex),
            quantity: 0,
            ...sectionParams()
          })
        });

        if (!response.ok) throw new Error('Cart change failed');
        const result = await response.json();

        // Wait for the collapse animation to finish
        await new Promise(r => setTimeout(r, 300));

        renderSections(result.sections);
        this.saved.render();
        this.init();
        showToast('Saved for later');
      } catch (err) {
        console.error('Save error:', err);
        tr.classList.remove('is-saving');
        if (pill) {
          pill.disabled = false;
          pill.textContent = 'Save for later';
        }
        showToast('Failed to save \u2014 try again');
      }
    }

    handleCompare(tr) {
      const title = (tr.dataset.itemTitle || '').trim();
      if (!title) return;

      // Mobile: the dubia-search drawer, driven through its own input
      // plumbing (same path as its recent-searches flow). Its only input is
      // the mobile header bar, so a visible primary input == drawer UX.
      const search = document.querySelector('dubia-search');
      const drawerInput = search && typeof search.getPrimaryInput === 'function'
        ? search.getPrimaryInput()
        : null;

      if (search && drawerInput) {
        if (typeof search.open === 'function') search.open();
        if (typeof search.syncInputs === 'function') {
          search.syncInputs(title);
        } else {
          drawerInput.value = title;
        }
        search.activeInput = drawerInput;
        drawerInput.dispatchEvent(new Event('input', { bubbles: true }));
        drawerInput.focus();
        return;
      }

      // Desktop: the drawer has no input — use the header's predictive
      // search modal instead.
      const modals = document.querySelectorAll('details-modal.header__search');
      for (const modal of modals) {
        const summary = modal.querySelector('summary');
        const input = modal.querySelector('input.search__input, input[type="search"]');
        if (!summary || !input) continue;
        const rect = summary.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) continue; // hidden breakpoint variant
        summary.click();
        input.value = title;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
        return;
      }
    }

    handleShare(tr) {
      const title = tr.dataset.itemTitle || '';
      const url = tr.dataset.productUrl
        ? window.location.origin + tr.dataset.productUrl
        : window.location.href;

      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(() => {});
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          showToast('Link copied!');
        }).catch(() => {
          fallbackCopy(url);
        });
      } else {
        fallbackCopy(url);
      }
    }

    observeMutations() {
      const target = document.querySelector('#main-cart-items .js-contents');
      if (!target) return;

      const observer = new MutationObserver(() => {
        requestAnimationFrame(() => this.init());
      });

      observer.observe(target, { childList: true, subtree: true });
    }
  }

  /* ========================================================================
     Utilities
     ======================================================================== */
  function showToast(message) {
    const existing = document.querySelector('.cart-swipe-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'cart-swipe-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 200);
    }, 2000);
  }

  function fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      showToast('Link copied!');
    } catch {
      showToast('Could not copy link');
    }
    ta.remove();
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  /* ========================================================================
     Corso widget polish — relocate info icon to "Learn more" link,
     add a "Remove" / "Add back" toggle beside it
     ======================================================================== */
  function setCorsoRemoved(card, removed) {
    card.classList.toggle('corso-removed', removed);
    card.querySelector('.corso-remove').textContent = removed ? 'Add back' : 'Remove';
    try {
      if (removed) sessionStorage.setItem(CORSO_REMOVED_KEY, '1');
      else sessionStorage.removeItem(CORSO_REMOVED_KEY);
    } catch {}
  }

  function isCorsoRemovedSaved() {
    try {
      return sessionStorage.getItem(CORSO_REMOVED_KEY) === '1';
    } catch {
      return false;
    }
  }

  function polishCorsoWidget() {
    const icon = document.querySelector('.corso-info-icon');
    if (!icon || icon.dataset.relocated) return;

    const card = icon.closest('.corso-info-container');
    const desc = card?.querySelector('.corso-info-description');
    if (!desc) return;

    // Create "Learn more" link that triggers the same modal
    const link = document.createElement('span');
    link.className = 'corso-learn-more';
    link.textContent = 'Learn more';
    link.setAttribute('role', 'button');
    link.setAttribute('tabindex', '0');
    link.addEventListener('click', () => icon.click());
    link.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); icon.click(); }
    });

    const actions = document.createElement('div');
    actions.className = 'corso-info-actions';
    actions.append(link);
    desc.appendChild(actions);

    // Remove works through Corso's opt-out link, so only offer it when that exists
    if (document.querySelector('.corso-button')) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'corso-remove';
      remove.addEventListener('click', () => {
        setCorsoRemoved(card, !card.classList.contains('corso-removed'));
      });
      actions.append(remove);
      setCorsoRemoved(card, isCorsoRemovedSaved());
    }

    // Hide original icon in title row
    icon.style.cssText = 'position:absolute!important;width:1px!important;height:1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;';
    icon.dataset.relocated = '1';

    // Move "Checkout without package protection" link into the sticky .cart__ctas
    // so it scrolls with the checkout button on mobile
    const corsoBtn = document.querySelector('.corso-button-container');
    const ctas = document.querySelector('.cart__ctas');
    if (corsoBtn && ctas && !ctas.querySelector('.corso-button-container')) {
      ctas.appendChild(corsoBtn);
    }
  }

  // Corso's checkout clone always adds protection. While the customer has
  // removed it, send the click through Corso's own opt-out instead, which
  // drops any protection line and continues to checkout.
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-corso-checkout-plus="clone"]')) return;
    if (!document.querySelector('.corso-info-container.corso-removed')) return;
    const optOut = document.querySelector('.corso-button');
    if (!optOut) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    optOut.click();
  }, true);

  // Poll for Corso injection (it loads async after DOMContentLoaded)
  function waitForCorso() {
    if (document.querySelector('.corso-info-container')) {
      polishCorsoWidget();
      return;
    }
    let attempts = 0;
    const interval = setInterval(() => {
      if (document.querySelector('.corso-info-container') || ++attempts > 30) {
        clearInterval(interval);
        polishCorsoWidget();
      }
    }, 300);
  }

  /* ========================================================================
     Init on DOM ready
     ======================================================================== */
  function boot() {
    const savedManager = new SavedItemsManager();
    savedManager.render();
    new CartActionsManager(savedManager);

    waitForCorso();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
